<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    append-to="body"
    :closable="false"
    :draggable="false"
    class="commitment-form-dialog w-full"
    :pt="dialogPt"
    data-testid="commitment-form-dialog"
    @show="handleShow"
    @hide="handleHide"
  >
    <template #header>
      <div class="commitment-form-dialog__header">
        <div class="commitment-form-dialog__header-text">
          <h2 id="commitment-form-dialog-title" class="commitment-form-dialog__title">
            {{ dialogTitle }}
          </h2>
          <p class="commitment-form-dialog__subtitle">{{ contextSubtitle }}</p>
        </div>
        <button
          type="button"
          class="commitment-form-dialog__close"
          :aria-label="$t('ProjectManagement.cancel')"
          data-testid="commitment-form-close"
          @click="close"
        >
          <i class="pi pi-times" aria-hidden="true" />
        </button>
      </div>
    </template>

    <div class="commitment-form-dialog__body">
      <div
        v-for="field in textFields"
        :key="field.key"
        class="commitment-form-dialog__field"
      >
        <label
          class="commitment-form-dialog__label"
          :for="field.id"
        >
          {{ $t(field.labelKey) }}
          <span v-if="field.required" class="commitment-form-dialog__required">*</span>
        </label>
        <p class="commitment-form-dialog__hint">{{ $t(field.hintKey) }}</p>
        <Textarea
          :id="field.id"
          v-model="form[field.key]"
          class="commitment-form-dialog__textarea w-full"
          :class="{ 'commitment-form-dialog__textarea--compact': !field.required }"
          :rows="field.rows"
          :maxlength="maxLength"
          :placeholder="$t(field.placeholderKey)"
          :invalid="!!formErrors[field.key]"
          :data-testid="field.testId"
        />
        <div class="commitment-form-dialog__meta">
          <small v-if="formErrors[field.key]" class="commitment-form-dialog__error">
            {{ formErrors[field.key] }}
          </small>
          <span class="commitment-form-dialog__counter">
            {{ fieldCharCount(field.key) }} / {{ maxLength }}
          </span>
        </div>
      </div>

      <hr class="commitment-form-dialog__divider" />

      <div class="commitment-form-dialog__field">
        <label class="commitment-form-dialog__label" for="commitment-milestone">
          {{ $t('UserProjects.commitmentDialog.milestoneLabel') }}
        </label>
        <p class="commitment-form-dialog__hint">
          {{ $t('UserProjects.commitmentDialog.milestoneHint') }}
        </p>
        <Select
          input-id="commitment-milestone"
          v-model="form.milestoneId"
          :options="milestoneOptions"
          option-label="label"
          option-value="value"
          option-disabled="disabled"
          class="commitment-form-dialog__select w-full"
          :loading="milestonesLoading"
          data-testid="commitment-milestone-select"
        />
      </div>

      <div class="commitment-form-dialog__row">
        <div class="commitment-form-dialog__field">
          <label class="commitment-form-dialog__label" for="commitment-due-week">
            {{ $t('UserProjects.commitmentDialog.dueWeekLabel') }}
          </label>
          <Select
            input-id="commitment-due-week"
            v-model="form.calendarWeekKey"
            :options="calendarWeekOptions"
            option-label="label"
            option-value="value"
            class="commitment-form-dialog__select w-full"
            :invalid="!!formErrors.calendarWeekKey"
            data-testid="commitment-due-week-select"
          />
          <small v-if="formErrors.calendarWeekKey" class="commitment-form-dialog__error">
            {{ formErrors.calendarWeekKey }}
          </small>
        </div>

        <div class="commitment-form-dialog__field">
          <label class="commitment-form-dialog__label" for="commitment-owner">
            {{ $t('UserProjects.commitmentDialog.ownerLabel') }}
          </label>
          <Select
            v-if="hasOwnerOptions"
            input-id="commitment-owner"
            v-model="form.ownerUserId"
            :options="ownerSelectOptions"
            option-label="label"
            option-value="value"
            class="commitment-form-dialog__select w-full"
            :invalid="!!formErrors.ownerUserId"
            data-testid="commitment-owner-select"
          >
            <template #option="{ option }">
              <span :data-user-email="option.email || undefined">{{ option.label }}</span>
            </template>
          </Select>
          <div
            v-else
            class="commitment-form-dialog__owner-readonly"
            data-testid="commitment-owner-readonly"
          >
            {{ ownerReadonlyLabel }}
          </div>
          <small v-if="formErrors.ownerUserId" class="commitment-form-dialog__error">
            {{ formErrors.ownerUserId }}
          </small>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="commitment-form-dialog__footer">
        <button
          type="button"
          class="commitment-form-dialog__btn-cancel"
          data-testid="commitment-form-cancel"
          @click="close"
        >
          {{ $t('ProjectManagement.cancel') }}
        </button>
        <button
          type="button"
          class="commitment-form-dialog__btn-submit"
          :class="{ 'commitment-form-dialog__btn-submit--active': canSubmit }"
          :disabled="!canSubmit || saving"
          data-testid="commitment-form-submit"
          @click="submit"
        >
          <i v-if="saving" class="pi pi-spin pi-spinner" aria-hidden="true" />
          <i v-else class="pi pi-check" aria-hidden="true" />
          <span>{{ submitLabel }}</span>
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import {
  buildCalendarWeekOptions,
  formatWeekLabelFromKey,
  getCurrentWeekKey,
} from '@/utils/calendarWeek';
import { buildCommitmentOwnerSelectOptions } from '@/utils/commitmentOwnerSelectLabel';
import { buildMilestoneSelectOptions } from '@/utils/milestoneSelectLabel';
import {
  COMMITMENT_REQUIRED_MIN_LENGTH,
  COMMITMENT_TEXT_MAX_LENGTH,
  mapCommitmentApiToDialog,
  mapCommitmentDialogToApiPayload,
  validateCommitmentDialogForm,
} from '@/utils/commitmentFormMapping';

const TEXT_FIELDS = [
  {
    key: 'was',
    id: 'commitment-was',
    testId: 'commitment-was',
    labelKey: 'UserProjects.commitmentDialog.wasLabel',
    hintKey: 'UserProjects.commitmentDialog.wasHint',
    placeholderKey: 'UserProjects.commitmentDialog.wasPlaceholder',
    required: true,
    rows: 4,
  },
  {
    key: 'woranErkennbar',
    id: 'commitment-woran-erkennbar',
    testId: 'commitment-woran-erkennbar',
    labelKey: 'UserProjects.commitmentDialog.erkennbarLabel',
    hintKey: 'UserProjects.commitmentDialog.erkennbarHint',
    placeholderKey: 'UserProjects.commitmentDialog.erkennbarPlaceholder',
    required: true,
    rows: 4,
  },
  {
    key: 'wasKonkret',
    id: 'commitment-was-konkret',
    testId: 'commitment-was-konkret',
    labelKey: 'UserProjects.commitmentDialog.konkretLabel',
    hintKey: 'UserProjects.commitmentDialog.konkretHint',
    placeholderKey: 'UserProjects.commitmentDialog.konkretPlaceholder',
    required: false,
    rows: 3,
  },
  {
    key: 'warumBedeutend',
    id: 'commitment-why',
    testId: 'commitment-why',
    labelKey: 'UserProjects.commitmentDialog.whyLabel',
    hintKey: 'UserProjects.commitmentDialog.whyHint',
    placeholderKey: 'UserProjects.commitmentDialog.whyPlaceholder',
    required: false,
    rows: 3,
  },
];

const EMPTY_FORM = () => ({
  was: '',
  woranErkennbar: '',
  wasKonkret: '',
  warumBedeutend: '',
  milestoneId: null,
  calendarWeekKey: getCurrentWeekKey(),
  ownerUserId: '',
});

export default {
  name: 'CommitmentFormDialog',
  components: {
    Dialog,
    Textarea,
    Select,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    projectId: {
      type: String,
      default: null,
    },
    projectName: {
      type: String,
      default: '',
    },
    initialCommitment: {
      type: Object,
      default: null,
    },
    currentUserId: {
      type: String,
      default: '',
    },
    defaultOwnerUserId: {
      type: String,
      default: '',
    },
    participantUsers: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:visible', 'submit'],
  data() {
    return {
      form: EMPTY_FORM(),
      formErrors: {},
      maxLength: COMMITMENT_TEXT_MAX_LENGTH,
      textFields: TEXT_FIELDS,
    };
  },
  computed: {
    ...mapGetters('pmMilestone', {
      milestones: 'milestones',
      milestonesLoading: 'loading',
    }),
    visibleProxy: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    dialogTitle() {
      return this.mode === 'create'
        ? this.$t('UserProjects.commitmentDialog.createTitle')
        : this.$t('UserProjects.commitmentDialog.editTitle');
    },
    submitLabel() {
      return this.mode === 'create'
        ? this.$t('UserProjects.commitmentDialog.submitCreate')
        : this.$t('ProjectManagement.commitments.save');
    },
    contextSubtitle() {
      const weekLabel = formatWeekLabelFromKey(
        getCurrentWeekKey(),
        (key) => this.$t(key),
      );
      const projectLabel = this.projectName || this.$t('UserProjects.context.required');
      return `${projectLabel} · ${weekLabel}`;
    },
    calendarWeekOptions() {
      return buildCalendarWeekOptions({
        weeksBefore: 0,
        weeksAfter: 12,
        translate: (key) => this.$t(key),
      });
    },
    milestoneOptions() {
      return buildMilestoneSelectOptions(this.milestones, (key) => this.$t(key));
    },
    hasOwnerOptions() {
      return (this.participantUsers || []).length > 0;
    },
    ownerSelectOptions() {
      return buildCommitmentOwnerSelectOptions(
        this.participantUsers,
        this.currentUserId || this.defaultOwnerUserId,
        (key, params) => this.$t(key, params),
      );
    },
    canSubmit() {
      return String(this.form.was || '').trim().length >= COMMITMENT_REQUIRED_MIN_LENGTH
        && String(this.form.woranErkennbar || '').trim().length >= COMMITMENT_REQUIRED_MIN_LENGTH;
    },
    ownerReadonlyLabel() {
      const user = (this.participantUsers || []).find(
        (item) => String(item.id) === String(this.form.ownerUserId),
      );
      const name = user?.name || user?.email || this.$t('UserProjects.commitmentDialog.ownerSelfFallback');
      return this.$t('UserProjects.commitmentDialog.ownerSelf', { name });
    },
    dialogPt() {
      return {
        root: {
          'data-testid': 'commitment-form-dialog',
          'aria-labelledby': 'commitment-form-dialog-title',
        },
      };
    },
  },
  watch: {
    visible(isOpen) {
      if (isOpen) {
        this.resetFormFromProps();
      }
    },
    initialCommitment: {
      handler() {
        if (this.visible) {
          this.resetFormFromProps();
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions('pmMilestone', ['setProjectContext']),

    fieldCharCount(key) {
      return String(this.form[key] || '').length;
    },

    async handleShow() {
      this.resetFormFromProps();
      if (this.projectId) {
        try {
          await this.setProjectContext(this.projectId);
        } catch {
          // milestone list may stay empty
        }
      }
    },

    handleHide() {
      this.formErrors = {};
    },

    resetFormFromProps() {
      if (this.mode === 'edit' && this.initialCommitment) {
        this.form = {
          ...EMPTY_FORM(),
          ...mapCommitmentApiToDialog(this.initialCommitment),
        };
      } else {
        this.form = {
          ...EMPTY_FORM(),
          ownerUserId: this.defaultOwnerUserId || '',
        };
      }
    },

    close() {
      this.visibleProxy = false;
    },

    submit() {
      this.formErrors = validateCommitmentDialogForm(
        {
          ...this.form,
          hasOwnerOptions: this.hasOwnerOptions,
        },
        (key, params) => this.$t(key, params),
      );
      if (Object.keys(this.formErrors).length > 0) {
        return;
      }

      const payload = mapCommitmentDialogToApiPayload({
        ...this.form,
        ownerUserId: this.form.ownerUserId || this.defaultOwnerUserId,
      });

      this.$emit('submit', payload);
    },
  },
};
</script>

<style scoped>
.commitment-form-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.commitment-form-dialog__title {
  margin: 0;
  font-size: 1.125rem;
  line-height: 1.35;
  font-weight: 600;
  color: var(--color-dark);
}

.commitment-form-dialog__subtitle {
  margin: 0.125rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--color-disabled);
}

.commitment-form-dialog__close {
  flex-shrink: 0;
  margin: -0.25rem -0.25rem 0 0;
  padding: 0.25rem;
  border: none;
  background: transparent;
  color: var(--color-disabled);
  cursor: pointer;
  transition: color 0.15s ease;
}

.commitment-form-dialog__close:hover {
  color: var(--color-dark);
}

.commitment-form-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-bottom: 0.25rem;
}

.commitment-form-dialog__field {
  display: flex;
  flex-direction: column;
}

.commitment-form-dialog__label {
  margin-bottom: 0.25rem;
  font-size: 0.8125rem;
  line-height: 1.4;
  font-weight: 500;
  color: var(--color-disabled);
}

.commitment-form-dialog__required {
  color: var(--color-red);
}

.commitment-form-dialog__hint {
  margin: 0 0 0.375rem;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--color-inactive-dark);
}

.commitment-form-dialog__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.125rem;
  min-height: 1rem;
}

.commitment-form-dialog__error {
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--color-red);
}

.commitment-form-dialog__counter {
  margin-left: auto;
  font-size: 0.6875rem;
  line-height: 1.4;
  color: var(--color-inactive-dark);
}

.commitment-form-dialog__divider {
  margin: 0;
  border: none;
  border-top: 1px solid var(--border-color, #e5e7eb);
}

.commitment-form-dialog__row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

@media (min-width: 640px) {
  .commitment-form-dialog__row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.commitment-form-dialog__owner-readonly {
  display: flex;
  align-items: center;
  min-height: 2.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 0.625rem;
  background: var(--color-white);
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--color-dark);
}

.commitment-form-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.commitment-form-dialog__btn-cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 0.625rem;
  background: var(--color-white);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-dark);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.commitment-form-dialog__btn-cancel:hover {
  background: var(--color-light-grey);
}

.commitment-form-dialog__btn-submit {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.625rem;
  background: var(--color-inactive);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-white);
  cursor: not-allowed;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.commitment-form-dialog__btn-submit--active:not(:disabled) {
  background: var(--accent-gradient);
  cursor: pointer;
}

.commitment-form-dialog__btn-submit--active:not(:disabled):hover {
  box-shadow: 0 8px 24px rgba(228, 130, 26, 0.25);
}
</style>

<style>
/* PrimeVue shell — PDF mock: 580px card, tall layout, scroll on content pane only */
.commitment-form-dialog.p-dialog {
  display: flex;
  flex-direction: column;
  width: min(580px, calc(100vw - 2rem));
  max-height: min(90vh, 52rem);
  margin: 1rem;
  border: none;
  border-radius: 1.25rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  font-family: var(--font-family, 'AsenPro', sans-serif);
}

.commitment-form-dialog.p-dialog .p-dialog-header {
  flex-shrink: 0;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  background: var(--color-white);
}

.commitment-form-dialog.p-dialog .p-dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 1rem 0.75rem 0.75rem 1.5rem;
  background: var(--color-white);
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.commitment-form-dialog.p-dialog .p-dialog-content::-webkit-scrollbar {
  width: 0.375rem;
}

.commitment-form-dialog.p-dialog .p-dialog-content::-webkit-scrollbar-track {
  background: transparent;
}

.commitment-form-dialog.p-dialog .p-dialog-content::-webkit-scrollbar-thumb {
  background: var(--color-light-grey);
  border-radius: 999px;
}

.commitment-form-dialog.p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover {
  background: var(--color-inactive);
}

.commitment-form-dialog.p-dialog .p-dialog-footer {
  flex-shrink: 0;
  padding: 0 1.5rem 1rem;
  border-top: 1px solid var(--border-color, #e5e7eb);
  background: var(--color-white);
}

.commitment-form-dialog.p-dialog .p-dialog-footer::before {
  content: none;
}

.commitment-form-dialog .commitment-form-dialog__textarea.p-textarea,
.commitment-form-dialog .commitment-form-dialog__textarea .p-textarea {
  width: 100%;
  min-height: 5.5rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 0.625rem;
  background: var(--color-white);
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--color-dark);
  resize: vertical;
  box-shadow: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.commitment-form-dialog .commitment-form-dialog__textarea--compact.p-textarea,
.commitment-form-dialog .commitment-form-dialog__textarea--compact .p-textarea {
  min-height: 4.25rem;
}

.commitment-form-dialog .commitment-form-dialog__textarea.p-textarea:enabled:focus,
.commitment-form-dialog .commitment-form-dialog__textarea .p-textarea:enabled:focus {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 3px rgba(228, 130, 26, 0.15);
  outline: none;
}

.commitment-form-dialog .commitment-form-dialog__textarea.p-invalid,
.commitment-form-dialog .commitment-form-dialog__textarea.p-invalid .p-textarea {
  border-color: var(--color-red);
}

.commitment-form-dialog .commitment-form-dialog__textarea.p-invalid:enabled:focus,
.commitment-form-dialog .commitment-form-dialog__textarea.p-invalid .p-textarea:enabled:focus {
  box-shadow: 0 0 0 3px rgba(228, 75, 85, 0.15);
}

.commitment-form-dialog .commitment-form-dialog__select.p-select {
  width: 100%;
  min-height: 2.5rem;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 0.625rem;
  background: var(--color-white);
  font-size: 0.875rem;
  box-shadow: none;
}

.commitment-form-dialog .commitment-form-dialog__select.p-select:not(.p-disabled):hover {
  border-color: var(--border-color, #e5e7eb);
}

.commitment-form-dialog .commitment-form-dialog__select.p-focus {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 3px rgba(228, 130, 26, 0.15);
}

.commitment-form-dialog .commitment-form-dialog__select .p-select-label {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: var(--color-dark);
}

.commitment-form-dialog .p-dialog-mask {
  background: rgba(0, 0, 0, 0.4);
}
</style>
